import { forwardRef } from 'react';
import { Typography, Grid, Divider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { getFormattedDate } from 'util/assessments-util';
import getClassroomStore from 'graph/state/query-hooks/use-get-classroom';
import useUserStore from 'graph/state/query-hooks/use-user';
import { Domain, AssessmentReport, Student } from 'types';
import { formatReports, findProductCurriculum } from './class-reports-util';
import PrintableSection from './PrintableSection';

interface ClassReportProps {
  productCurriculumId: number;
  classroomId: number;
  unit?: string;
  benchmark?: string;
  domainId: string;
  allDomains: Domain[];
  reports: AssessmentReport[];
  students: Student[];
}

const t = (key) => translate('ui', key);

const PrintableClassReport = forwardRef<HTMLDivElement, ClassReportProps>((props, ref) => {
  const classes = useStyles();
  const [user] = useUserStore();
  const [classroom] = getClassroomStore(props.classroomId);
  const productCurriculum = findProductCurriculum(user, props.productCurriculumId);

  const now = new Date(Date.now());

  const formattedReports = formatReports(
    props.productCurriculumId,
    props.allDomains,
    props.reports,
    props.domainId,
    props.unit,
    props.benchmark
  );

  const domainLabel =
    props.domainId !== 'all'
      ? props.allDomains?.find((d) => d.id.toString() == props.domainId)?.title
      : null;

  const testLabel =
    props.benchmark && props.benchmark !== 'all'
      ? t('class.benchmark') + ' ' + t('report.benchmarkPeriod-' + props.benchmark)
      : props.unit && props.unit !== 'all'
      ? t('class.unit') + ' ' + props.unit
      : null;

  const reportSubtitle = domainLabel
    ? testLabel
      ? testLabel + ' - ' + domainLabel
      : domainLabel
    : testLabel
    ? testLabel + ' - ' + t('report.domainsAll')
    : t('report.domainsAll');

  return (
    <div ref={ref}>
      <Grid container item xs={12}>
        <Grid item xs={8}>
          <Typography component="h2" variant="h2">
            {productCurriculum?.name}
          </Typography>
          <Typography component="p" variant="body1" className={classes.pt2}>
            {reportSubtitle}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography component="p" variant="body1" align="right" className="date">
            {`${t('assessmentSummary.printedOn')}: ${getFormattedDate(now)}`}
          </Typography>
          <Typography component="p" variant="body1" align="right" className={classes.pt2}>
            {`${t('report.class')}: ${classroom?.data?.name}`}
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      {formattedReports.map((outcome) => (
        <PrintableSection key={outcome.id} students={props.students} outcomes={outcome} />
      ))}
    </div>
  );
});

export default PrintableClassReport;

const useStyles = makeStyles((theme: Theme) => {
  return {
    pt2: {
      paddingTop: theme.spacing(2),
    },
  };
});
